// src/components/SecureDocumentViewer.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSearchParams } from "react-router-dom";
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Lock,
  ShieldAlert,
  ShieldCheck,
  TimerReset,
  X,
} from 'lucide-react';

import documentViewerAPI from '../services/documentViewerService';
import ChunkDecryptionService from '../services/chunkDecryptionService';
import SessionManager from '../services/sessionManager';
import securityService from '../services/securityService';
import { useAuth } from '../utils/AuthContext';
import { buildViewerWatermarkText, drawSecureViewerWatermark } from '../utils/secureViewerWatermark';

const ACCESS_REASONS = [
  'identity_verification',
  'document_verification',
  'employee_onboarding',
  'payroll_verification',
  'dpdp_compliance',
  'data_quality_review',
  'support_investigation',
];

export default function SecureDocumentViewer({
  documentId,
  onClose,
  onExit,
  startPage = 1,
  mode = "modal",
  accessScope = "active",
}) {
  const [searchParams] = useSearchParams();
  const resolvedAccessScope = accessScope || searchParams.get("access_scope") || "active";
  const { policy, hasPermission } = useAuth();

  const [phase, setPhase] = useState('request'); // request | requesting | processing | viewing | error
  const [error, setError] = useState('');
  const [governanceError, setGovernanceError] = useState('');

  const [session, setSession] = useState(null);
  const [currentPage, setCurrentPage] = useState(startPage);
  const [totalPages, setTotalPages] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(null);

  const [devToolsOpen, setDevToolsOpen] = useState(false);
  const [securityWarning, setSecurityWarning] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [zoom, setZoom] = useState(1);

  const [accessForm, setAccessForm] = useState({
    access_reason: '',
    purpose_code: '',
    justification: '',
  });

  const canvasRef = useRef(null);
  const scrollRef = useRef(null);

  const requestedRef = useRef(false);
  const statusPollRef = useRef(null);
  const pollStartedRef = useRef(false);
  const heartbeatMgrRef = useRef(null);
  const pageFetchInFlightRef = useRef(false);

  const sessionToken = session?.session_token;
  const viewerDecryptionKey = session?.viewer_decryption_key;

  const exitViewer = useCallback(() => {
  if (onClose) {
    onClose();
    return;
  }

  if (onExit) {
    onExit();
  }
}, [onClose, onExit]);

  const allowedPurposes = useMemo(() => {
    const values = policy?.purpose_codes || [];
    return Array.isArray(values) ? values : [];
  }, [policy]);

  const requiresJustification = policy?.requires_justification !== false;
  const canViewDocuments = hasPermission('view_document_metadata');
  const retainedAccess = resolvedAccessScope === 'retained';

  const timeText = useMemo(() => {
    if (timeRemaining == null) return '';

    const m = Math.floor(timeRemaining / 60);
    const s = Math.floor(timeRemaining % 60)
      .toString()
      .padStart(2, '0');

    return `${m}:${s}`;
  }, [timeRemaining]);

  const canPrev = currentPage > 1;
  const canNext = totalPages && currentPage < totalPages;

  const updateAccessForm = (key, value) => {
    setAccessForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateDocumentAccessRequest = () => {
    if (!canViewDocuments) {
      return 'You do not have permission to view documents.';
    }

    if (!accessForm.access_reason) {
      return 'Access reason is required.';
    }

    if (allowedPurposes.length > 0 && !allowedPurposes.includes('*')) {
      if (!accessForm.purpose_code) {
        return 'Purpose code is required by your policy.';
      }

      if (!allowedPurposes.includes(accessForm.purpose_code)) {
        return 'Selected purpose code is not allowed by your policy.';
      }
    }

    if (requiresJustification && !accessForm.justification.trim()) {
      return 'Justification is required by your policy.';
    }

    return '';
  };

  const drawWatermark = useCallback(
    (ctx, w, h) => {
      drawSecureViewerWatermark(
        ctx,
        buildViewerWatermarkText(session),
        w,
        h,
      );
    },
    [session],
  );

  const fitAndDraw = useCallback(
    (imgBitmap) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      const container = scrollRef.current;
      const maxW = container ? container.clientWidth : 1200;

      const iw = imgBitmap.width;
      const ih = imgBitmap.height;

      const scale = Math.min(maxW / iw, 1) * zoom;
      const w = Math.max(1, Math.floor(iw * scale));
      const h = Math.max(1, Math.floor(ih * scale));

      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext('2d', {
        alpha: false,
        desynchronized: true,
      });

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(imgBitmap, 0, 0, w, h);

      drawWatermark(ctx, w, h);
    },
    [drawWatermark, zoom],
  );

  const renderPageFromBytes = useCallback(
    async (uint8) => {
      const isPng =
        uint8.length > 8 &&
        uint8[0] === 0x89 &&
        uint8[1] === 0x50 &&
        uint8[2] === 0x4e &&
        uint8[3] === 0x47 &&
        uint8[4] === 0x0d &&
        uint8[5] === 0x0a &&
        uint8[6] === 0x1a &&
        uint8[7] === 0x0a;

      const isJpeg =
        uint8.length > 3 &&
        uint8[0] === 0xff &&
        uint8[1] === 0xd8 &&
        uint8[2] === 0xff;

      const mime = isPng
        ? 'image/png'
        : isJpeg
          ? 'image/jpeg'
          : 'application/octet-stream';

      if (mime === 'application/octet-stream') {
        console.error(
          '[viewer] plaintext is not a supported rendered page image.',
        );
        throw new Error('PLAINTEXT_NOT_IMAGE');
      }

      const blob = new Blob([uint8], { type: mime });
      const bmp = await createImageBitmap(blob);
      fitAndDraw(bmp);
    },
    [fitAndDraw],
  );

  const decryptAndAssemble = useCallback(async (encryptedChunks) => {
    if (!viewerDecryptionKey) {
      throw new Error('VIEWER_SESSION_KEY_MISSING');
    }

    const parts = [];

    for (const enc of encryptedChunks) {
      const dec = await ChunkDecryptionService.decryptChunk(
        enc,
        viewerDecryptionKey,
      );
      parts.push(dec);
    }

    return ChunkDecryptionService.assembleChunks(parts);
  }, [viewerDecryptionKey]);

  async function fetchChunkWithRetry(token, pageNumber, idx, retries = 2) {
    try {
      return await documentViewerAPI.getPageChunk(token, pageNumber, idx);
    } catch (e) {
      const status = e?.response?.status;

      if (retries > 0 && (status === 404 || status === 409)) {
        await new Promise((resolve) =>
          setTimeout(resolve, 200 * (3 - retries)),
        );

        return fetchChunkWithRetry(token, pageNumber, idx, retries - 1);
      }

      throw e;
    }
  }

  const fetchPage = useCallback(
    async (pageNumber) => {
      if (!sessionToken || pageFetchInFlightRef.current) return;

      pageFetchInFlightRef.current = true;
      setIsDrawing(true);

      try {
        const first = await fetchChunkWithRetry(sessionToken, pageNumber, 0);

        const encryptedChunks = [first.chunk];
        let idx = 0;
        let isLast = !!first.is_last_chunk;

        const totalChunksCount = Number(first.total_chunks);

        while (!isLast) {
          idx += 1;

          try {
            const res = await fetchChunkWithRetry(sessionToken, pageNumber, idx);

            encryptedChunks.push(res.chunk);
            isLast = !!res.is_last_chunk;

            if (
              !Number.isNaN(totalChunksCount) &&
              totalChunksCount > 0 &&
              idx >= totalChunksCount - 1
            ) {
              break;
            }
          } catch (e) {
            if (e?.response?.status === 404 && idx >= 1) {
              break;
            }

            throw e;
          }
        }

        const pageBytes = await decryptAndAssemble(encryptedChunks);

        if (pageBytes.length < 32) {
          throw new Error('PLAINTEXT_TOO_SMALL');
        }

        await renderPageFromBytes(pageBytes);
      } catch (e) {
        console.error('fetchPage error', e?.message || e);
        setError(`Failed to load page: ${e?.message || 'Unknown error'}`);
      } finally {
        setIsDrawing(false);
        pageFetchInFlightRef.current = false;
      }
    },
    [decryptAndAssemble, renderPageFromBytes, sessionToken],
  );

  const stopHeartbeat = useCallback(() => {
    if (heartbeatMgrRef.current?.stop) {
      heartbeatMgrRef.current.stop();
    }

    heartbeatMgrRef.current = null;
  }, []);

  const closeSession = useCallback(async () => {
  try {
    if (sessionToken) {
      await documentViewerAPI.closeSession(sessionToken);
    }
  } catch (e) {
    console.warn("closeSession error", e?.message || e);
  } finally {
    stopHeartbeat();
    exitViewer();
  }
}, [exitViewer, sessionToken, stopHeartbeat]);

  const startHeartbeat = useCallback(
    (token, expiresAt) => {
      const mgr = new SessionManager();

      mgr.start(
        token,
        expiresAt,
        () => {
          setSecurityWarning('Session expired');
          closeSession();
        },
        () =>
          setSecurityWarning(
            'Session will expire soon. Please finish viewing.',
          ),
      );

      heartbeatMgrRef.current = mgr;
    },
    [closeSession],
  );

  const beginGovernedDocumentSession = async () => {
    const validationError = validateDocumentAccessRequest();

    if (validationError) {
      setGovernanceError(validationError);
      return;
    }

    if (requestedRef.current) return;

    requestedRef.current = true;
    setGovernanceError('');
    setError('');
    setPhase('requesting');

    try {
      const data = await documentViewerAPI.requestViewSession(documentId, {
        access_reason: accessForm.access_reason,
        purpose_code: accessForm.purpose_code,
        justification: accessForm.justification,
        access_scope: resolvedAccessScope,
      });

      const sess = {
        ...data.session,
        viewer_decryption_key: data.viewer_decryption_key,
      };

      if (!sess.viewer_decryption_key) {
        throw new Error('Secure viewer session key was not returned.');
      }

      setSession(sess);
      setTimeRemaining(sess.time_remaining ?? null);
      setPhase('processing');
    } catch (e) {
      console.error(e);

      requestedRef.current = false;
      setError(
        e?.response?.data?.detail ||
          'Could not start secure viewing session.',
      );
      setPhase('error');
    }
  };

  useEffect(() => {
    securityService.initialize(() => {
      setDevToolsOpen(true);
      setSecurityWarning('Developer Tools detected. Session will be closed.');

      if (sessionToken) {
        closeSession();
      }
    });

    return () => securityService.cleanup();
  }, [closeSession, sessionToken]);

  useEffect(() => {
    requestedRef.current = false;
    pageFetchInFlightRef.current = false;
    pollStartedRef.current = false;

    setPhase('request');
    setError('');
    setGovernanceError('');
    setSecurityWarning('');
    setSession(null);
    setTimeRemaining(null);
    setTotalPages(0);
    setCurrentPage(startPage);

    return () => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
      }

      pollStartedRef.current = false;
      stopHeartbeat();
    };
  }, [documentId, startPage, stopHeartbeat]);

  useEffect(() => {
    const token = session?.session_token;

    if (!token || pollStartedRef.current) return;

    startHeartbeat(token, session.expires_at);

    const tick = async () => {
      try {
        const st = await documentViewerAPI.getSessionStatus(token);

        setTimeRemaining(st?.session?.time_remaining ?? null);

        if (!st?.is_valid) {
          clearInterval(statusPollRef.current);
          setError('Session became invalid.');
          setPhase('error');
          return;
        }

        if (st?.session?.is_processed) {
          clearInterval(statusPollRef.current);
          setTotalPages(st.session.total_pages || 1);
          setPhase('viewing');
          setCurrentPage(startPage);
        }
      } catch (e) {
        clearInterval(statusPollRef.current);
        setError('Failed to get session status.');
        setPhase('error');
      }
    };

    statusPollRef.current = setInterval(tick, 1200);
    pollStartedRef.current = true;
    tick();

    return () => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
      }

      pollStartedRef.current = false;
    };
  }, [session?.session_token, session?.expires_at, startHeartbeat, startPage]);

  useEffect(() => {
    if (phase === 'viewing') {
      fetchPage(currentPage);
    }
  }, [currentPage, phase, fetchPage]);

  useEffect(() => {
    const onResize = () => {
      if (phase === 'viewing') {
        fetchPage(currentPage);
      }
    };

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [currentPage, phase, fetchPage]);

  if (phase === 'request') {
    return (
      <div className="flex h-full min-h-[620px] flex-col rounded-2xl bg-slate-50">
        <div className="border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-orange-50 p-3 text-orange-600 ring-1 ring-orange-100">
              <Lock className="h-5 w-5" />
            </div>

            <div>
              <h2 className="text-lg font-bold text-slate-950">
                Governed Document Access
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Document session requires reason, purpose and policy validation.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-orange-50 p-3 text-orange-600 ring-1 ring-orange-100">
                <ShieldCheck className="h-6 w-6" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-950">
                  Request secure document view session
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Access is checked against your group permissions, purpose scope,
                  document type scope, justification policy and session TTL.
                </p>
              </div>
            </div>

            {governanceError && (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5" />
                  <p className="text-sm font-medium">{governanceError}</p>
                </div>
              </div>
            )}

            {!canViewDocuments && (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="mt-0.5 h-5 w-5" />
                  <p className="text-sm font-medium">
                    You do not have permission to view documents.
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 space-y-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Access Reason
                  </label>

                  <select
                    value={accessForm.access_reason}
                    onChange={(e) =>
                      updateAccessForm('access_reason', e.target.value)
                    }
                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  >
                    <option value="">Select reason</option>

                    {ACCESS_REASONS.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    Purpose Code
                  </label>

                  {allowedPurposes.length > 0 &&
                  !allowedPurposes.includes('*') ? (
                    <select
                      value={accessForm.purpose_code}
                      onChange={(e) =>
                        updateAccessForm('purpose_code', e.target.value)
                      }
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    >
                      <option value="">Select purpose</option>

                      {allowedPurposes.map((purpose) => (
                        <option key={purpose} value={purpose}>
                          {purpose.replaceAll('_', ' ')}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={accessForm.purpose_code}
                      onChange={(e) =>
                        updateAccessForm('purpose_code', e.target.value)
                      }
                      placeholder="Optional purpose code"
                      className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">
                  Justification{' '}
                  {requiresJustification && (
                    <span className="text-red-600">*</span>
                  )}
                </label>

                <textarea
                  value={accessForm.justification}
                  onChange={(e) =>
                    updateAccessForm('justification', e.target.value)
                  }
                  rows={4}
                  placeholder="Explain why this document access is required."
                  className="w-full resize-none rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />
              </div>

              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 text-sm text-orange-800">
                Session TTL: {policy?.session_ttl_minutes || 15} minute(s).
                Document page access will be audited.
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={closeSession}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100"
                >
                  <X className="w-4 h-4" />
                  {mode === "fullpage" ? "End session & return" : "Close"}
                </button>

                <button
                  onClick={beginGovernedDocumentSession}
                  disabled={!canViewDocuments}
                  className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Start Secure Document View
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="relative flex h-full w-full select-none flex-col bg-neutral-50">
    {/* Header */}
    <div className="sticky top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-3 shadow-sm">
      <div className="flex min-w-0 items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
          <ShieldAlert className="h-4 w-4" />
          Secure Document View
        </span>

        {session?.access_reason && (
          <span className="hidden rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-800 md:inline-flex">
            Reason: {session.access_reason.replaceAll('_', ' ')}
            {session.purpose_code ? ` · Purpose: ${session.purpose_code}` : ''}
            {session.document_type ? ` · Type: ${session.document_type}` : ''}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {timeText && (
          <span
            title="Time remaining"
            className="inline-flex items-center gap-1 rounded-lg bg-slate-50 px-2.5 py-1 text-sm font-semibold text-slate-600 ring-1 ring-slate-200"
          >
            <TimerReset className="h-4 w-4" />
            {timeText}
          </span>
        )}

        <button
          type="button"
          onClick={closeSession}
          className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-bold text-red-700 hover:bg-red-100"
        >
          <X className="h-4 w-4" />
          {mode === 'fullpage' ? 'End session & return' : 'Close'}
        </button>
      </div>
    </div>

    {session?.access_reason && (
      <div className="border-b border-orange-100 bg-orange-50 px-4 py-2 text-xs font-medium text-orange-800">
        Governed document session · reason:{' '}
        {session.access_reason.replaceAll('_', ' ')}
        {session.purpose_code ? ` · purpose: ${session.purpose_code}` : ''}
        {session.document_type ? ` · type: ${session.document_type}` : ''}
      </div>
    )}

    {securityWarning && (
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
        {securityWarning}
      </div>
    )}

    {devToolsOpen && (
      <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-800">
        Developer Tools detected. Session will be closed.
      </div>
    )}

    {error && (
      <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
        {error}
      </div>
    )}

    <div ref={scrollRef} className="flex-1 overflow-auto bg-neutral-50">
      <div className="flex w-full justify-center py-4">
        <canvas ref={canvasRef} className="rounded bg-white shadow-inner" />
      </div>
    </div>

    {phase === 'viewing' && (
      <div className="flex items-center justify-center gap-3 border-t bg-white px-4 py-3">
        <div className="mr-auto flex items-center gap-2">
          <button
            onClick={() =>
              setZoom((z) => Math.max(0.25, +(z - 0.25).toFixed(2)))
            }
            className="rounded border px-2 py-1"
          >
            −
          </button>

          <span className="w-14 text-center text-sm">
            {Math.round(zoom * 100)}%
          </span>

          <button
            onClick={() =>
              setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))
            }
            className="rounded border px-2 py-1"
          >
            ＋
          </button>
        </div>

        <button
          disabled={!canPrev || isDrawing}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 ${
            canPrev && !isDrawing
              ? 'bg-gray-100 hover:bg-gray-200'
              : 'cursor-not-allowed bg-gray-50 text-gray-400'
          }`}
        >
          <ChevronLeft className="h-4 w-4" />
          Prev
        </button>

        <span className="text-sm text-gray-600">
          Page {currentPage} / {totalPages || '…'}
        </span>

        <button
          disabled={!canNext || isDrawing}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 ${
            canNext && !isDrawing
              ? 'bg-gray-100 hover:bg-gray-200'
              : 'cursor-not-allowed bg-gray-50 text-gray-400'
          }`}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    )}

    {phase !== 'viewing' && (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="rounded-xl border bg-white/80 px-4 py-3 text-sm text-gray-700 shadow backdrop-blur">
          {phase === 'requesting' && 'Evaluating document access policy…'}
          {phase === 'processing' && 'Preparing document…'}
          {phase === 'error' && 'An error occurred.'}
        </div>
      </div>
    )}

    
  </div>
);
}

export function SecureDocumentViewerPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();

  const returnToRecords = useCallback(() => {
    navigate('/records', { replace: true });
  }, [navigate]);

  return (
    <div className="h-screen w-screen bg-slate-50">
      <SecureDocumentViewer
        documentId={documentId}
        mode="fullpage"
        onExit={returnToRecords}
      />
    </div>
  );
}